import { Button, Flex, Text } from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useFormContext } from '../../../../../../../../../../../../hooks'
import { LegalMentionsType } from '../../../../../../enums/legalMentionsType'
import { InvoiceSettingsFormSchema } from '../../../../../../types/invoiceSettingsFormSchema'
import * as Styled from './styles'

interface LegalMentionRowProps {
  onEdit: (legalMentionsType: LegalMentionsType) => void
  legalMentionsType: LegalMentionsType
}

export const LegalMentionsRow = ({ legalMentionsType, onEdit }: LegalMentionRowProps): ReactElement => {
  const { t } = useTranslation()
  const { watch } = useFormContext<InvoiceSettingsFormSchema>()

  const earlyPaymentDiscountLegalMention = watch('legalMentions.custom.earlyPaymentDiscount')
  const latePaymentPenaltyRateLegalMention = watch('legalMentions.custom.latePaymentPenaltyRate')

  const { description, title } = useMemo(() => {
    switch (legalMentionsType) {
      case LegalMentionsType.EarlyPaymentDiscount: {
        const defaultDescription = t(
          'settings.organization.external_invoice.legal_mentions.early_payment_discount.description',
        )
        return {
          title: t('settings.organization.external_invoice.legal_mentions.early_payment_discount.title'),
          description: earlyPaymentDiscountLegalMention ?? defaultDescription,
        }
      }
      case LegalMentionsType.LatePaymentPenaltyRate: {
        const defaultDescription = t(
          'settings.organization.external_invoice.legal_mentions.late_payment_penalty_rate.default_description',
        )
        return {
          title: t('settings.organization.external_invoice.legal_mentions.late_payment_penalty_rate.title'),
          description: latePaymentPenaltyRateLegalMention ?? defaultDescription,
        }
      }
    }
  }, [earlyPaymentDiscountLegalMention, latePaymentPenaltyRateLegalMention, legalMentionsType, t])

  const handleEditButtonClick = useCallback(() => {
    onEdit(legalMentionsType)
  }, [onEdit, legalMentionsType])

  return (
    <Flex alignItems="center" justifyContent="space-between">
      <Styled.ContentWrapper>
        <Text weight="bold">{title}</Text>
        {description && (
          <Text truncate colorVariant="secondary">
            {description}
          </Text>
        )}
      </Styled.ContentWrapper>

      <Button icon="squarePen" onClick={handleEditButtonClick} size="sm" variant="text">
        {t('settings.organization.external_invoice.legal_mentions.edit')}
      </Button>
    </Flex>
  )
}
